import React from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* Which screens may run inside another site's frame.

   The shell is served without X-Frame-Options (backend/spa_views.py) because
   restaurants embed the booking widget on their own websites, and the widget
   is a #route inside the same page as everything else - the server cannot tell
   the two apart. So the line is drawn here: inside a frame, only the pages a
   guest reaches render. The login and the staff screens refuse, so no other
   site can load them under its own buttons (clickjacking).

   The widget's flow stays inside this list: it books on /widget/... and then
   moves to /customer_reservation_menu/... to show the reservation.

   It reads the router's location rather than listening for hashchange: the
   hash router navigates with pushState, which never fires that event. */
export const FRAMEABLE_ROUTE_PREFIXES = [
  "/widget/",
  "/customer_reservation_menu/",
  "/c/",
  "/reservations/",
  "/delete_client_info/",
  "/notifications/"
];

const isFramed = () => {
  try {
    return window.self !== window.top;
  } catch (error) {
    // Only a frame can make reading window.top throw.
    return true;
  }
};

export const mayRenderInFrame = (pathname, framed = isFramed()) =>
  !framed ||
  FRAMEABLE_ROUTE_PREFIXES.some(prefix => String(pathname || "").startsWith(prefix));

const Refusal = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  background: #ffffff;
  color: #4b3335;
  font-size: 15px;
  line-height: 1.5;

  p {
    margin: 0;
    max-width: 360px;
  }

  a {
    color: #ee754c;
    font-weight: 600;
  }
`;

const FrameGate = ({ children }) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  if (mayRenderInFrame(pathname)) return children;

  return (
    <Refusal role="alert">
      <p>{t("ui.frameGate.message")}</p>
      <a href={window.location.href} target="_blank" rel="noopener noreferrer">
        {t("ui.frameGate.open")}
      </a>
    </Refusal>
  );
};

export default FrameGate;
